import { $, qs, escapeHtml } from './dom';
import { showToast } from './toast';

let _adminShowCb: ((which: string) => void) | null = null;
export function setAdminShowCb(fn: (which: string) => void): void { _adminShowCb = fn; }

export async function enterAdmin(){
  $('admin-status').textContent = 'loading…';
  $('admin-body').innerHTML = '<div class="admin-loading">loading…</div>';
  let res: Response;
  try {
    res = await fetch('/api/v1/admin/stats', { credentials: 'include' });
  } catch {
    renderAdminError('Could not reach the server.');
    return;
  }
  // Only a real auth failure (the neutral 404, or a 401) means "not authorized".
  // A 200 with a bad/unexpected body is a load error, not a denial — don't tell
  // an authorized operator they're locked out.
  if(res.status !== 200){ renderAdminDenied(); return; }
  let stats: any;
  try { stats = await res.json(); } catch { renderAdminError('Could not load stats (bad response).'); return; }
  try { renderAdmin(stats); } catch { renderAdminError('Could not render stats.'); }
}

export function renderAdminError(msg: string){
  $('admin-status').textContent = 'error';
  $('admin-body').innerHTML =
    '<div class="admin-denied">' +
      '<i class="ph ph-warning-circle" aria-hidden="true"></i>' +
      `<h3>${escapeHtml(msg)}</h3>` +
      '<p>The admin console could not load.</p>' +
      '<button class="menu-action" id="admin-error-retry"><span>Retry</span><i class="ph ph-arrow-clockwise"></i></button>' +
    '</div>';
  $('admin-error-retry')?.addEventListener('click', () => enterAdmin());
}

export function renderAdminDenied(){
  $('admin-status').textContent = 'not authorized';
  $('admin-body').innerHTML =
    '<div class="admin-denied">' +
      '<i class="ph ph-lock-key" aria-hidden="true"></i>' +
      '<h3>Not authorized</h3>' +
      '<p>This area is restricted to operators.</p>' +
      '<button class="menu-action" id="admin-denied-back"><span>Back to map</span><i class="ph ph-map-pin"></i></button>' +
    '</div>';
  $('admin-denied-back')?.addEventListener('click', () => _adminShowCb?.('map'));
}

export function adminStat(label: string, n: number){
  return `<div class="admin-stat"><span class="admin-stat-n">${n}</span><span class="admin-stat-l">${escapeHtml(label)}</span></div>`;
}

export function adminAction(id: string, label: string, count: number | null, danger: boolean, sub?: string){
  const badge = (count === null || count === undefined)
    ? ''
    : `<span class="admin-badge ${count > 0 ? 'is-some' : 'is-zero'}">${count}</span>`;
  const subline = sub ? `<span class="admin-action-sub">${escapeHtml(sub)}</span>` : '';
  return `<button class="admin-action${danger ? ' is-danger' : ''}" id="${id}">` +
    `<span class="admin-action-main"><span class="admin-action-label">${escapeHtml(label)}</span>${subline}</span>${badge}</button>`;
}

export function renderAdmin(s: any){
  $('admin-status').textContent = 'live counts';
  const sw = s.sweepable || {};
  const orphanTotal = (sw.orphan_pings || 0) + (sw.orphan_sessions || 0) + (sw.orphan_shares || 0) + (sw.orphan_claims || 0);
  const orphanSub = `pings ${sw.orphan_pings || 0} · sessions ${sw.orphan_sessions || 0} · shares ${sw.orphan_shares || 0} · claims ${sw.orphan_claims || 0}`;
  $('admin-body').innerHTML =
    '<div class="admin-wrap">' +
      '<section class="admin-card">' +
        '<h2>Position DB <span class="admin-sub">locus-db</span></h2>' +
        '<div class="admin-stats">' +
          adminStat('devices', s.db.devices) + adminStat('pings', s.db.pings) +
          adminStat('sessions', s.db.sessions) + adminStat('shares', s.db.shares) +
          adminStat('commands · pending', s.db.commands_pending) +
        '</div>' +
      '</section>' +
      '<section class="admin-card">' +
        '<h2>Identity DB <span class="admin-sub">locus-identity-db</span></h2>' +
        '<div class="admin-stats">' +
          adminStat('users', s.identity.users) + adminStat('sessions', s.identity.sessions) +
          adminStat('claims · active', s.identity.claims_active) + adminStat('claims · revoked', s.identity.claims_revoked) +
          adminStat('backups', s.identity.identity_backups) +
        '</div>' +
      '</section>' +
      '<section class="admin-card">' +
        '<h2>Housekeeping</h2>' +
        '<p class="admin-note">Removes dead or aged rows. The first three only remove already-invalid data and run on a single click.</p>' +
        '<div class="admin-actions">' +
          adminAction('admin-sweep-shares', 'Sweep expired / revoked shares', sw.shares, false) +
          adminAction('admin-sweep-sessions', 'Purge expired auth sessions', sw.sessions, false) +
          adminAction('admin-sweep-orphans', 'Prune orphaned rows', orphanTotal, false, orphanSub) +
          adminAction('admin-sweep-commands', 'Sweep acked / expired commands', sw.commands, false) +
          adminAction('admin-sweep-pings', 'Trim pings older than 30 days', sw.pings_30d, true, 'removes live history · type TRIM') +
        '</div>' +
      '</section>' +
      '<section class="admin-card admin-card--danger">' +
        '<h2>Reset</h2>' +
        '<p class="admin-note">Destructive and unrecoverable. Production and preview share one database, so a reset here also clears preview.</p>' +
        '<div class="admin-actions">' +
          adminAction('admin-reset-devices', 'Reset device data', s.db.devices, true, 'pings, sessions, devices, shares — identity kept · type RESET') +
          adminAction('admin-reset-all', 'Full nuke', null, true, 'all device data + all identity — signs you out · type NUKE') +
        '</div>' +
      '</section>' +
    '</div>';
  $('admin-sweep-shares')?.addEventListener('click', () => doSweep('shares'));
  $('admin-sweep-sessions')?.addEventListener('click', () => doSweep('sessions'));
  $('admin-sweep-orphans')?.addEventListener('click', () => doSweep('orphans'));
  $('admin-sweep-commands')?.addEventListener('click', () => doSweep('commands'));
  $('admin-sweep-pings')?.addEventListener('click', () => doSweep('pings'));
  $('admin-reset-devices')?.addEventListener('click', () => doReset('devices'));
  $('admin-reset-all')?.addEventListener('click', () => doReset('all'));
}

// Promise-based typed-confirm overlay. Resolves true only when the operator
// types `word` exactly and confirms. Used for the live-data actions (RESET /
// NUKE / TRIM); the dead-data sweeps skip it.
export function askTypedConfirm(word: string, heading: string, detail: string): Promise<boolean>{
  return new Promise((resolve) => {
    const back = document.createElement('div');
    back.className = 'admin-confirm-back';
    back.innerHTML =
      '<div class="admin-confirm" role="dialog" aria-modal="true" aria-label="Confirm">' +
        `<h3>${escapeHtml(heading)}</h3>` +
        `<p>${escapeHtml(detail)}</p>` +
        `<p class="admin-confirm-ask">Type <b>${escapeHtml(word)}</b> to confirm.</p>` +
        '<input type="text" class="admin-confirm-input" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" />' +
        '<div class="admin-confirm-actions">' +
          '<button class="btn btn--quiet admin-confirm-cancel">Cancel</button>' +
          '<button class="btn btn--primary admin-confirm-go" disabled>Confirm</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(back);
    const input: any = qs('.admin-confirm-input', back);
    const go: any = qs('.admin-confirm-go', back);
    const close = (val: boolean) => { back.remove(); resolve(val); };
    input.addEventListener('input', () => { go.disabled = input.value !== word; });
    input.addEventListener('keydown', (e: any) => {
      if(e.key === 'Enter' && input.value === word) close(true);
      if(e.key === 'Escape') close(false);
    });
    go.addEventListener('click', () => { if(input.value === word) close(true); });
    qs('.admin-confirm-cancel', back).addEventListener('click', () => close(false));
    back.addEventListener('click', (e: any) => { if(e.target === back) close(false); });
    setTimeout(() => input.focus(), 30);
  });
}

export async function adminPost(path: string, body: any): Promise<any>{
  try {
    const res = await fetch(path, {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json: any = await res.json().catch(() => null);
    if(!res.ok){ showToast('failed: ' + (json && json.error ? json.error : res.status)); return null; }
    return json;
  } catch {
    showToast('request failed');
    return null;
  }
}

export function deletedTotal(deleted: any): number{
  if(!deleted) return 0;
  return Object.values(deleted).reduce((a: number, b: any) => a + (typeof b === 'number' ? b : 0), 0);
}

export async function doSweep(action: string){
  let body: any = { action };
  if(action === 'pings'){
    const okGo = await askTypedConfirm('TRIM', 'Trim old pings',
      'Deletes pings older than 30 days. This removes live trail history and cannot be undone.');
    if(!okGo) return;
    body = { action, confirm: 'TRIM' };
  }
  const res = await adminPost('/api/v1/admin/sweep', body);
  if(!res) return;
  showToast('removed ' + deletedTotal(res.deleted));
  enterAdmin();
}

export async function doReset(scope: string){
  const word = scope === 'all' ? 'NUKE' : 'RESET';
  const heading = scope === 'all' ? 'Full nuke' : 'Reset device data';
  const detail = scope === 'all'
    ? 'Deletes ALL device data AND all identity (users, sessions, claims, backups). Unrecoverable. You will be signed out.'
    : 'Deletes all devices, pings, sessions and shares. Identity is kept. Unrecoverable.';
  const okGo = await askTypedConfirm(word, heading, detail);
  if(!okGo) return;
  const res = await adminPost('/api/v1/admin/reset', { scope, confirm: word });
  if(!res) return;
  if(scope === 'all'){
    showToast('nuked — signing out');
    // Identity (incl. this session) is gone. Reload at the root so the app boots
    // fresh and signed-out. Admin access survives via the env-var allowlist:
    // sign in again and the console is reachable.
    setTimeout(() => { window.location.href = '/'; }, 600);
    return;
  }
  showToast('device data reset · removed ' + deletedTotal(res.deleted));
  enterAdmin();
}
